import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import InputAdornment from "@mui/material/InputAdornment";
import { CalendarMonth, Email, PersonOutline, Phone, Search } from "@mui/icons-material";
import { Link } from "react-router-dom";
import CardActionArea from "@mui/material/CardActionArea";
import { getInitials } from "@/lib/utils";
import {
  bookingMatchesAdminCustomer,
  normalizeBookingsList,
  normalizeAdminCustomersListPayload,
} from "@/lib/adminApi";
import { fetchAdminBookingListJson, ADMIN_BOOKINGS_MAX_PAGE_SIZE } from "@/lib/queryClient";
import type { Customer, BookingWithDetails } from "@shared/schema";

export default function AdminCustomers() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const { data: customers, isLoading } = useQuery<Customer[]>({
    queryKey: ["/api/admin/customers"],
    select: (raw: unknown) => normalizeAdminCustomersListPayload(raw),
  });
  const { data: bookings } = useQuery({
    queryKey: ["/api/admin/bookings", "list", 1, ADMIN_BOOKINGS_MAX_PAGE_SIZE],
    queryFn: () => fetchAdminBookingListJson(1, ADMIN_BOOKINGS_MAX_PAGE_SIZE),
    select: (data: unknown) => normalizeBookingsList(data) as BookingWithDetails[],
  });

  const filtered = customers?.filter((c) => {
    const q = search.toLowerCase();
    const hay = `${c.firstName} ${c.lastName} ${c.email} ${c.phone ?? ""}`.toLowerCase();
    return hay.includes(q);
  });

  function getBookingCount(customer: Customer) {
    return bookings?.filter((b) => bookingMatchesAdminCustomer(b, customer) && b.status !== "cancelled").length || 0;
  }

  function getLastVisit(customer: Customer) {
    const customerBookings = bookings
      ?.filter(
        (b) =>
          bookingMatchesAdminCustomer(b, customer) && (b.status === "completed" || b.status === "confirmed"),
      )
      .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());
    if (customerBookings && customerBookings.length > 0) {
      return new Date(customerBookings[0].startAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    return null;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: "auto" }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} data-testid="text-customers-title">
            Customers
          </Typography>
          <Typography color="text.secondary">{customers?.length || 0} customers</Typography>
        </Box>

        <TextField
          placeholder={t("adminCustomers.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="input-search-customers"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {isLoading ? (
          <Stack spacing={1.5}>
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} variant="rounded" height={80} />
            ))}
          </Stack>
        ) : filtered?.length === 0 ? (
          <Card variant="outlined">
            <CardContent sx={{ py: 8, textAlign: "center" }}>
              <PersonOutline sx={{ fontSize: 48, color: "action.disabled", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {search ? "No customers found" : "No customers yet"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {search
                  ? "Try a different search term"
                  : "Customers will appear here after they make a booking"}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Stack spacing={1}>
            {filtered?.map((customer) => (
              <Card key={customer.id} variant="outlined">
                <CardActionArea component={Link} to={`/admin/customers/${customer.id}`}>
                <CardContent sx={{ py: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40 }}>
                      {getInitials(`${customer.firstName} ${customer.lastName}`)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={500} data-testid={`text-customer-name-${customer.id}`}>
                        {customer.firstName} {customer.lastName}
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 0.5 }}>
                        <Stack direction="row" alignItems="center" gap={0.5} color="text.secondary" typography="body2">
                          <Email sx={{ fontSize: 14 }} />
                          {customer.email}
                        </Stack>
                        {customer.phone && (
                          <Stack direction="row" alignItems="center" gap={0.5} color="text.secondary" typography="body2">
                            <Phone sx={{ fontSize: 14 }} />
                            {customer.phone}
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {t("adminCustomers.bookingsCount", { count: getBookingCount(customer) })}
                      </Typography>
                      {getLastVisit(customer) && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          gap={0.5}
                          color="text.secondary"
                          typography="caption"
                          sx={{ mt: 0.5 }}
                        >
                          <CalendarMonth sx={{ fontSize: 14 }} />
                          {getLastVisit(customer)}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
